// The company's own words and custom fields, from its industry template or
// its own settings.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useOrg } from "./session";

export type Term = "contact" | "deal" | "appointment" | "service" | "resource";
export interface Word { one: string; other: string }
export interface TermWords { en: Word; ar: Word }
export type Vocabulary = Partial<Record<Term, TermWords>>;

export type FieldType = "text" | "long_text" | "number" | "date" | "select" | "multi_select" | "checkbox" | "email" | "phone" | "url";
export interface FieldOption { value: string; label_en: string; label_ar: string }
export interface FieldDef {
  id?: string;
  key: string;
  label_en: string;
  label_ar: string;
  type: FieldType;
  options: FieldOption[];
  ai_fillable: boolean;
  position?: number;
  source?: string;
  archived?: boolean;
}

export interface Workspace {
  template: { key: string; name: string; applied_at: string } | null;
  vocabulary: Record<Term, TermWords>;
  overrides: Vocabulary;
  fields: { contact: FieldDef[]; deal: FieldDef[] };
  agent_template_key: string | null;
}

export const terms: { key: Term; label: string }[] = [
  { key: "contact", label: "Contacts (customers)" },
  { key: "deal", label: "Deals (opportunities)" },
  { key: "appointment", label: "Appointments" },
  { key: "service", label: "Services (what can be booked)" },
  { key: "resource", label: "Staff (who or what is booked)" },
];

export const defaultWords: Record<Term, TermWords> = {
  contact: { en: { one: "Contact", other: "Contacts" }, ar: { one: "جهة اتصال", other: "جهات الاتصال" } },
  deal: { en: { one: "Deal", other: "Deals" }, ar: { one: "صفقة", other: "الصفقات" } },
  appointment: { en: { one: "Appointment", other: "Appointments" }, ar: { one: "موعد", other: "المواعيد" } },
  service: { en: { one: "Service", other: "Services" }, ar: { one: "خدمة", other: "الخدمات" } },
  resource: { en: { one: "Staff member", other: "Staff" }, ar: { one: "موظف", other: "الموظفون" } },
};

export const fieldTypes: { value: FieldType; label: string }[] = [
  { value: "text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "One choice" },
  { value: "multi_select", label: "Several choices" },
  { value: "checkbox", label: "Yes / no" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "Web link" },
];

export function useWorkspace() {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "workspace"], queryFn: () => api<Workspace>(`${base}/workspace`), staleTime: 5 * 60_000 });
}

// useWords returns the company's word for a term: w("appointment") is
// "Meeting", w("appointment", true) "Meetings", w.lower(...) for sentences.
export function useWords() {
  const ws = useWorkspace();
  const vocab = ws.data?.vocabulary ?? defaultWords;
  const w = (term: Term, plural = false) => {
    const t = vocab[term] ?? defaultWords[term];
    return plural ? t.en.other : t.en.one;
  };
  w.lower = (term: Term, plural = false) => w(term, plural).toLowerCase();
  return w;
}

// fieldValueText renders a stored value for reading.
export function fieldValueText(def: FieldDef, value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  switch (def.type) {
    case "select":
      return def.options.find((o) => o.value === value)?.label_en ?? String(value);
    case "multi_select":
      return Array.isArray(value) ? value.map((v) => def.options.find((o) => o.value === v)?.label_en ?? String(v)).join(", ") || "—" : "—";
    case "checkbox":
      return value ? "Yes" : "No";
    case "number":
      return typeof value === "number" ? value.toLocaleString("en-US", { maximumFractionDigits: 6 }) : String(value);
    case "date":
      return new Date(String(value) + "T00:00:00").toLocaleDateString("en-GB", { dateStyle: "medium" });
    default:
      return String(value);
  }
}
